"use client";

import { useSubscriptionTracking } from "@/hooks/use-subscription-tracking";
import { AuthProvider } from "@/lib/auth/auth-provider";
import { FileManagerProvider } from "@/providers/file-manager-provider";
import { MessageReplayStoreProvider } from "@/providers/message-replay-provider";
import { AppEventsProvider } from "@jace/insights";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type React from "react";
import { Suspense } from "react";
import { ApiClientProvider } from "../providers/api-client-provider";
import { ThemeProvider } from "../providers/theme-provider";

interface ProviderProps {
	children: React.ReactNode;
}

function makeQueryClient(): QueryClient {
	return new QueryClient({
		defaultOptions: {
			queries: {
				// NOTE: This is done aggressively and might be tweaked per
				// query in the future
				staleTime: Number.POSITIVE_INFINITY,
				gcTime: Number.POSITIVE_INFINITY,
			},
		},
	});
}

let browserQueryClient: QueryClient | undefined;

function getQueryClient(): QueryClient {
	if (typeof window === "undefined") {
		return makeQueryClient();
	}

	if (!browserQueryClient) browserQueryClient = makeQueryClient();
	return browserQueryClient;
}

function SubscriptionTracker() {
	useSubscriptionTracking();
	return null;
}

export default function Providers({
	children,
}: ProviderProps): React.ReactElement {
	const queryClient = getQueryClient();

	return (
		<QueryClientProvider client={queryClient}>
			<AuthProvider>
				<ApiClientProvider>
					<FileManagerProvider>
						<ThemeProvider>
							<AppEventsProvider>
								<MessageReplayStoreProvider>
									{children}
									<Suspense fallback={null}>
										<SubscriptionTracker />
									</Suspense>
								</MessageReplayStoreProvider>
							</AppEventsProvider>
						</ThemeProvider>
					</FileManagerProvider>
				</ApiClientProvider>
			</AuthProvider>
		</QueryClientProvider>
	);
}
